// axiosSetup.js
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../../config";

const api = axios.create({
  withCredentials: true,
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const getAuthToken = () => localStorage.getItem("authToken");
const getCompanyId = () => localStorage.getItem("userId");
const getRefreshToken = () => localStorage.getItem("refreshToken");

let isRefreshing = false;
let refreshSubscribers = [];

// Notify all queued requests once refresh is done
const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

// Helper function to check if error is auth related
const isAuthError = (error) => {
  return (
    error.response?.status === 401 ||
    error.code === 'ERR_NETWORK' ||
    error.message?.toLowerCase().includes('unauthorized') ||
    error.message?.toLowerCase().includes('network error')
  );
};

const handleLogout = () => {
  console.log('Handling logout...');
  
  try {
    const itemsToClear = [
      'token',
      'authToken',
      'userId',
      'themeColor',
      'userInformation',
      'refreshToken'
    ];

    itemsToClear.forEach(item => {
      localStorage.removeItem(item);
      sessionStorage.removeItem(item);
    });
    
    console.log('Storage cleared, redirecting...');
    window.location.href = '/';
    
    toast.error('Session expired. Please login again.', {
      autoClose: 2000
    });
  } catch (e) {
    console.error('Error during logout:', e);
    window.location.href = '/';
  }
};

// REQUEST INTERCEPTOR
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const accountId = getCompanyId();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (accountId) {
      config.headers["accountId"] = accountId; 
      config.headers["isSuperAdmin"] = Number(accountId) === 43 ? true : false;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// RESPONSE INTERCEPTOR
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.log('API Error:', {
      status: error.response?.status,
      message: error.message,
      code: error.code
    });

    const originalRequest = error.config;

    // Check if it's an auth error AND not already retried
    if (isAuthError(error) && !originalRequest._retry) {
      originalRequest._retry = true;

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((newToken) => {
            originalRequest.headers.Authorization = "Bearer " + newToken;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          console.log('No refresh token found, logging out');
          handleLogout();
          return Promise.reject(error);
        }

        console.log('Attempting to refresh token...');

        // Call refresh token API
        const res = await axios.get(
          `${BASE_URL}sim-auth/v2/refresh?refreshToken=${refreshToken}`
        );

        const newAccessToken = res.data;
        const newRefreshToken = res.data.refreshToken; // If backend sends new refresh token
        
        localStorage.setItem("authToken", newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }

        console.log('Token refreshed successfully');

        isRefreshing = false;
        onRefreshed(newAccessToken);

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
        
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        isRefreshing = false;
        handleLogout();
        return Promise.reject(refreshError);
      }
    }

    // For non-auth errors, just reject
    return Promise.reject(error);
  }
);

export default api;